import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

import { UserLocation } from '../../models/user/location.model';

/** `locationStatus` the backend sets when the Restaurant Admin lookup for a location 404s. */
export const UNAVAILABLE_LOCATION_STATUS = 'RESTAURANT_ADMIN_NOT_FOUND';

export function isLocationAvailable(location: UserLocation | undefined): boolean {
  return location?.locationStatus !== UNAVAILABLE_LOCATION_STATUS;
}

export const LOCATION_ERROR_PATH = '/location-error';

export function buildLocationErrorUrl(failedLocationNumber: string): string {
  return `${LOCATION_ERROR_PATH}?locationNumber=${encodeURIComponent(failedLocationNumber)}`;
}

export function sortFavoritesFirst(
  locations: UserLocation[],
  favoriteLocationNumbers: string[],
): UserLocation[] {
  if (!favoriteLocationNumbers?.length) {
    return locations;
  }
  const favorites: UserLocation[] = [];
  const rest: UserLocation[] = [];
  locations.forEach((loc) => {
    (favoriteLocationNumbers.includes(loc.locationNumber) ? favorites : rest).push(loc);
  });
  return [...favorites, ...rest];
}

export function resolveDefaultLocation(
  locations: UserLocation[],
  favoriteLocationNumbers: string[] | undefined,
): UserLocation | undefined {
  return sortFavoritesFirst(locations, favoriteLocationNumbers ?? [])[0];
}

/**
 * Picks the location to land on when the URL doesn't name a usable one: the location the
 * user already selected (if it is still one of theirs), else favorite, else first.
 */
export function pickLandingLocation(
  locations: UserLocation[],
  favoriteLocationNumbers: string[] | undefined,
  selected?: UserLocation,
): UserLocation | undefined {
  const selectedMatch = selected?.locationNumber
    ? locations.find((loc) => loc.locationNumber === selected.locationNumber)
    : undefined;
  return selectedMatch ?? resolveDefaultLocation(locations, favoriteLocationNumbers);
}

/**
 * Builds the URL to redirect to for `target`: the location error page when it is
 * unavailable (never silently picking another), else `/{locationNumber}{restOfPath}`.
 * `restOfPath` is the path without the location prefix; a bare `/` adds no trailing slash.
 */
export function buildLocationRedirectUrl(target: UserLocation, restOfPath: string): string {
  if (!isLocationAvailable(target)) {
    return buildLocationErrorUrl(target.locationNumber);
  }
  return `/${target.locationNumber}${restOfPath === '/' ? '' : restOfPath}`;
}

export function toggleFavoriteLocation(
  favoriteLocationNumbers: string[],
  locationNumber: string,
): string[] {
  return (favoriteLocationNumbers ?? []).includes(locationNumber)
    ? favoriteLocationNumbers.filter((num) => num !== locationNumber)
    : [...(favoriteLocationNumbers ?? []), locationNumber];
}

export function paginateLocations(
  locations: UserLocation[],
  currentPage: number,
  pageSize: number,
  totalCount: number = locations.length,
): UserLocation[] {
  const nextRowIndex = currentPage * pageSize;
  if (nextRowIndex + pageSize < totalCount) {
    return locations.slice(nextRowIndex, nextRowIndex + pageSize);
  }
  return locations.slice(nextRowIndex, locations.length);
}

export function injectSelectedLocation(
  locations: UserLocation[],
  selected: UserLocation | undefined,
): UserLocation[] {
  if (!selected?.locationNumber) {
    return locations;
  }
  const locationIndex = locations.findIndex(
    (loc) => loc.locationNumber === selected.locationNumber,
  );
  if (locationIndex === -1) {
    return [selected, ...locations];
  }
  const updated = [...locations];
  updated[locationIndex] = selected;
  return updated;
}

export function compareLocationsByNumber(a: UserLocation, b: UserLocation): boolean {
  return a?.locationNumber === b?.locationNumber;
}

export function resolveSelectedLocation(
  selected: UserLocation | undefined,
  locations: UserLocation[] | undefined,
): UserLocation | undefined {
  if (!selected?.locationNumber || !locations?.length) {
    return selected;
  }
  return locations.find(
    (loc) => loc.locationNumber === selected.locationNumber,
  ) ?? selected;
}

export function getSelectedLocationDisplayName(
  selected: UserLocation | undefined,
  locations: UserLocation[] | undefined,
): string {
  return resolveSelectedLocation(selected, locations)?.locationName ?? '';
}

export function filterLocationsBySearch(
  locations: UserLocation[],
  searchTerm: string,
): UserLocation[] {
  if (!searchTerm) {
    return locations;
  }
  const term = searchTerm.toLowerCase();
  return locations.filter((loc) =>
    loc.locationNumber.includes(searchTerm)
    || (loc.locationName ? loc.locationName.toLowerCase().includes(term) : false),
  );
}

export function shouldUseLocationSearch(
  locations: UserLocation[],
  pageSize: number,
): boolean {
  return locations.length > pageSize;
}

export function getDisplayLocations(
  locations: UserLocation[],
  selected: UserLocation | undefined,
  favoriteLocationNumbers: string[],
  useSearchAndPagination: boolean,
  searchTerm: string,
  currentPage: number,
  pageSize: number,
): UserLocation[] {
  const sorted = sortFavoritesFirst(locations, favoriteLocationNumbers);
  if (!useSearchAndPagination) {
    return injectSelectedLocation(sorted, selected);
  }
  const filtered = filterLocationsBySearch(sorted, searchTerm);
  const paginated = paginateLocations(filtered, currentPage, pageSize, sorted.length);
  return injectSelectedLocation(paginated, selected);
}

export function getFilteredLocations$(
  locations$: Observable<UserLocation[]>,
  searchTerm: string,
): Observable<UserLocation[]> {
  return locations$.pipe(map((locations) => filterLocationsBySearch(locations, searchTerm)));
}

export function getDisplayLocations$(
  locations$: Observable<UserLocation[]>,
  selected: UserLocation | undefined,
  favoriteLocationNumbers: string[],
  searchTerm: string,
  currentPage: number,
  pageSize: number,
): Observable<UserLocation[]> {
  return locations$.pipe(map((locations) => getDisplayLocations(
    locations,
    selected,
    favoriteLocationNumbers,
    shouldUseLocationSearch(locations, pageSize),
    searchTerm,
    currentPage,
    pageSize,
  )));
}

// Location numbers are numeric (e.g. "40394"), which is what lets this pattern tell a
// real location segment apart from the other top-level routes (eula, maintenance,
// logged-out, login/callback) that intentionally live outside the location prefix.
const LOCATION_PATH_SEGMENT_REGEX = /^\/(\d+)(?=\/|$)/;

export function extractLocationNumberFromPath(pathname: string): string | null {
  return LOCATION_PATH_SEGMENT_REGEX.exec(pathname)?.[1] ?? null;
}

export function rewriteLocationSegment(url: string, newLocationNumber: string): string {
  if (LOCATION_PATH_SEGMENT_REGEX.test(url)) {
    return url.replace(LOCATION_PATH_SEGMENT_REGEX, `/${newLocationNumber}`);
  }
  const separator = url.startsWith('/') ? '' : '/';
  return `/${newLocationNumber}${separator}${url}`;
}

/**
 * Strips the leading `/:locationNumber` segment so route-matching code that was written
 * against the old flat paths (e.g. `startsWith('/discover')`) keeps working unchanged.
 */
export function stripLocationPrefix(url: string): string {
  const stripped = url.replace(LOCATION_PATH_SEGMENT_REGEX, '');
  return stripped === '' ? '/' : stripped;
}

export function buildUrlForLocationChange(newLocationNumber: string): string {
  const { pathname, search, hash } = globalThis.location;
  return `${rewriteLocationSegment(pathname, newLocationNumber)}${search}${hash}`;
}
